import { HiveRoomState, RoomEvent } from "@br9704/aethereum-shared";
import { HostedSubscriber } from "../../hive/subscriber.js";
import { buildWatchModel, FLASH_MS, type WatchStatus } from "./model.js";

/**
 * `aethereum watch` — a live ASCII room view: the terminal twin of the web
 * dashboard. Catch-up over `GET /api/sync/events?since=0` builds the initial
 * HiveRoomState, then the HostedSubscriber feeds `.ingest()` and the Ink app
 * re-renders per event. Collisions flash the contract amber for ~3s.
 */

export interface WatchOptions {
  url?: string;
  token?: string;
  room?: string;
  fetchImpl?: typeof fetch;
}

export interface WatchConfig {
  baseUrl: string;
  token: string;
  room: string;
}

/** Flags win over env. Returns a friendly one-line error when keys are missing. */
export function resolveWatchConfig(
  opts: WatchOptions,
  env: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string } = process.env,
): WatchConfig | { error: string } {
  const baseUrl = opts.url ?? env.AETHEREUM_SYNC_API_URL;
  const token = opts.token ?? env.AETHEREUM_SYNC_TOKEN;
  if (!baseUrl || !token) {
    return {
      error:
        "aethereum watch needs a hosted room: export AETHEREUM_SYNC_API_URL + AETHEREUM_SYNC_TOKEN " +
        "(the dashboard's connect flow mints the key), or pass --url and --token.",
    };
  }
  return { baseUrl: baseUrl.replace(/\/$/, ""), token, room: opts.room ?? "room" };
}

interface CatchUpResult {
  cursor: number;
}

/**
 * Drain `GET /api/sync/events?since=` (200-event pages) into the room state.
 * Incoming `contract_changed` frames are skipped: HiveRoomState synthesizes
 * them from the paired `contract` declare, so ingesting both would duplicate
 * collision rows in the activity feed.
 */
export async function catchUp(
  cfg: WatchConfig,
  state: HiveRoomState,
  fetchImpl: typeof fetch = fetch,
): Promise<CatchUpResult> {
  let since = 0;
  for (let page = 0; page < 50; page++) {
    const res = await fetchImpl(`${cfg.baseUrl}/api/sync/events?since=${since}`, {
      headers: { Authorization: `Bearer ${cfg.token}` },
    }).catch(() => null);
    if (!res?.ok) break;
    const body = (await res.json().catch(() => null)) as {
      events?: Array<{ id: number; event: unknown }>;
      cursor?: number;
    } | null;
    if (!body?.events?.length) {
      since = Math.max(since, body?.cursor ?? since);
      break;
    }
    for (const frame of body.events) {
      const parsed = RoomEvent.safeParse(frame.event);
      if (parsed.success && parsed.data.type !== "contract_changed") state.ingest(parsed.data);
      since = Math.max(since, frame.id);
    }
  }
  return { cursor: since };
}

export async function watch(opts: WatchOptions): Promise<void> {
  const cfg = resolveWatchConfig(opts);
  if ("error" in cfg) {
    console.error(cfg.error);
    process.exit(1);
  }

  const fetchImpl = opts.fetchImpl ?? fetch;
  const state = new HiveRoomState();
  const { cursor } = await catchUp(cfg, state, fetchImpl);

  // ── render state the model derives from ───────────────────────────────────
  let status: WatchStatus = "connecting";
  let lastCursor = cursor;
  const flashes = new Map<string, number>(); // contract name → flash expiry

  // Lazy import keeps ink/react out of non-TUI code paths (matches join/run).
  const { startWatchApp } = await import("./app.js");

  const buildModel = () =>
    buildWatchModel(state, {
      flashes,
      status,
      now: Date.now(),
      roster: subscriber.roster,
      cursor: lastCursor,
    });

  const subscriber = new HostedSubscriber({
    baseUrl: cfg.baseUrl,
    token: cfg.token,
    since: cursor,
    includeOwn: true, // a watcher observes the whole room, its own member included
    fetchImpl,
    onEvent: ({ event, id }) => {
      lastCursor = id;
      if (event.type === "contract_changed") {
        // state synthesizes the collision from the paired contract declare —
        // the delivered frame only triggers the amber flash window
        flashes.set(event.name, Date.now() + FLASH_MS);
        const t = setTimeout(() => app.update(buildModel()), FLASH_MS + 50);
        if (typeof t === "object" && "unref" in t) t.unref();
      } else {
        state.ingest(event);
      }
      app.update(buildModel());
    },
    onStatus: (s) => {
      status = s;
      app.update(buildModel());
    },
  });

  const quit = () => {
    subscriber.stop();
    clearInterval(tick);
    app.stop();
    process.exit(0);
  };

  const app = startWatchApp({ room: cfg.room, initialModel: buildModel(), onQuit: quit });
  process.on("SIGINT", quit);
  process.on("SIGTERM", quit);

  // periodic repaint so presence liveness decays without traffic
  const tick = setInterval(() => app.update(buildModel()), 10_000);

  await subscriber.start(); // runs until stop(); never throws
}
